import React, { useState } from 'react';
import { Button, ProfilePicture } from '@getro/rombo';
import { Mail } from 'lucide-react';
import { useHistory, useParams, Redirect } from 'react-router-dom';
import { Box, Text, Flex } from 'rebass/styled-components';
import { useSelector } from 'react-redux';

import AuthSideBar from '../../../components/atoms/authSideBar';
import { OAuthMessageError } from '../../../components/molecules/oAuthMessageError';
import { SocialOAuthButtons } from '../../../components/molecules/socialOAuthButtons';
import { useOnboarding } from '../hooks/useOnboarding';
import { userSelector } from '../../../redux/selectors';

export const SignUp = () => {
  const [oAuthError, setOAuthError] = useState(null);
  const { email } = useSelector(userSelector) || {};
  const history = useHistory();
  const { networkSlug, employerSlug } = useParams();
  const { user, activeNetwork, profileMode, skipSignUp, company } = useOnboarding();

  if (skipSignUp) {
    return <Redirect to={`/networks/${networkSlug}/welcome/onboarding/profile/${profileMode}`} />;
  }

  const onClickSignUp = (mode) => {
    if (employerSlug) {
      history.push(`/networks/${networkSlug}/company/${employerSlug}/welcome/onboarding/profile/${mode}`);
    } else {
      history.push(`/networks/${networkSlug}/welcome/onboarding/profile/${mode}`);
    }
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: ['1fr', '1fr', '1fr 480px'], height: '100%' }}>
      <Flex sx={{ width: '100%', height: '100%', alignItems: 'center' }}>
        <Flex
          sx={{
            gap: '56px',
            flexDirection: 'column',
            px: '16px',
            width: '100%',
            maxWidth: '380px',
            mx: 'auto',
            height: 'auto',
          }}
        >
          <Flex width="100%" sx={{ gap: '16px', flexDirection: 'column', alignItems: 'flex-start' }}>
            <ProfilePicture
              width="48px"
              height="48px"
              variant="square"
              src={company?.logoUrl ?? activeNetwork.logoUrl}
              name={company?.name ?? activeNetwork.name}
              sx={{ flex: '0 0 auto' }}
            />
            <Text fontSize="24px" fontWeight="700" color="text.dark">
              {company ? `Join the ${company.name} network` : `Join the ${activeNetwork.name} network`}
            </Text>
          </Flex>
          <Box>
            <Text fontSize="14px" pb="8px" color="text.dark">
              You were invited at{' '}
              <Text as="span" fontWeight="medium">
                {email}
              </Text>
              , use it to sign up.
            </Text>
            <SocialOAuthButtons
              isInvite
              onSuccess={() => {
                onClickSignUp('social');
                setOAuthError(null);
              }}
              onError={(e) => {
                setOAuthError(e);
              }}
            />
            <Button
              mt="8px"
              icon={Mail}
              variant="secondary"
              width="100%"
              sx={{ gap: '16px', justifyContent: 'center' }}
              onClick={() => {
                if (employerSlug) {
                  onClickSignUp('email');
                } else {
                  onClickSignUp(user?.hasPassword || user?.oauthProviders.length > 0 ? 'password' : 'email');
                }
              }}
            >
              Continue with email
            </Button>

            {!!oAuthError && <OAuthMessageError mt="8px" oAuthError={oAuthError} email={email} />}
          </Box>
          <Text fontSize="14px" fontWeight="400" color="text.main" textAlign="center">
            Getro is currently invite-only. If you don’t have an invitation, check in with your network admin.
          </Text>
        </Flex>
      </Flex>
      <Box sx={{ display: ['none', 'none', 'block'] }}>
        <AuthSideBar />
      </Box>
    </Box>
  );
};
